import React, {useState} from "react";
import {DatePicker, Input, Select} from "antd";
import {Dayjs} from "dayjs";
import "../../../styles/component/common/search-criteria.scss";
import SearchCriteriaView from "./search-criteria.view";
import {
    AppliedFilter,
    IDefaultLabels,
    ISearchCriteriaProps,
    SearchCriteriaConfig,
    SearchParams
} from "./type/search-criteria.type";
import Locale from "../../../core/locale/locale";

const {RangePicker} = DatePicker;

const SearchCriteria = (props: ISearchCriteriaProps) => {

    const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>(props.initialFilters || []);
    const [topValues, setTopValues] = useState<Record<string, any>>({});
    const [showMoreFilters, setShowMoreFilters] = useState(false);

    const defaultLabels: IDefaultLabels = {
        searchBy: 'Search By',
        addButton: 'Add',
        searchButton: 'Search',
        resetButton: 'Reset',
        appliedFilters: 'Applied Filters',
        clearAll: 'Clear All',
        noFilters: 'No filters applied',
        ...props.labels,
    };

    const handleTopFieldChange = (key: string, value: any) => {
        setTopValues(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    const removeFilter = (id: string) => {
        const updated = appliedFilters.filter(f => f.id !== id);
        setAppliedFilters(updated);
        props.onFiltersChange?.(updated);
    };

    const clearAllFilters = () => {
        setAppliedFilters([]);
        setTopValues({});
        props.onFiltersChange?.([]);
        // props.onSearch({});
        setShowMoreFilters(false);
        props.onReset?.();
    };

    const handleSearch = () => {
        const payload: SearchParams = {};
        appliedFilters.forEach(f => {
            payload[f.key] = f.value;
        });

        Object.keys(topValues).forEach(key => {
            if (
                topValues[key] !== undefined &&
                topValues[key] !== null &&
                topValues[key] !== ''
            ) {
                payload[key] = topValues[key];
            }
        });

        props.onSearch(payload);
    };

    const isAnythingTyped = () => {
        if (appliedFilters.length > 0) return true;
        return Object.values(topValues).some(
            v => v !== '' && v !== null && v !== undefined
        );
    };

    const renderInputForCriteria = (criteria: SearchCriteriaConfig) => {
        const value = topValues[criteria.key];
        if (criteria.inputType === 'custom' && props.renderInputForCriteria) {
            return props.renderInputForCriteria(criteria, {
                values: topValues,
                onChange: handleTopFieldChange
            });
        }
        switch (criteria.inputType) {
            case 'text':
            case 'email':
                return (
                    <Input
                        value={value}
                        placeholder={criteria.placeholder}
                        onChange={(e) =>
                            handleTopFieldChange(criteria.key, e.target.value)
                        }
                        type={criteria.inputType === 'email' ? 'email' : 'text'}
                    />
                );

            case 'number':
                return (
                    <Input
                        type="number"
                        value={value}
                        placeholder={criteria.placeholder}
                        onChange={(e) =>
                            handleTopFieldChange(criteria.key, e.target.value)
                        }
                    />
                );

            case 'select':
                return (
                    <Select
                        value={value}
                        options={criteria.options}
                        placeholder={criteria.placeholder}
                        onChange={(val) =>
                            handleTopFieldChange(criteria.key, val)
                        }
                        allowClear
                    />
                );

            case 'date-range':
                return (
                    <RangePicker
                        value={value as [Dayjs | null, Dayjs | null]}
                        onChange={(dates) =>
                            handleTopFieldChange(criteria.key, dates)
                        }
                        format={Locale.displayDate}
                        style={{width: '100%'}}
                    />
                );

            case 'toggle':
                return (
                    <Select
                        value={value}
                        options={[
                            {label: 'Yes', value: true},
                            {label: 'No', value: false},
                        ]}
                        placeholder={criteria.placeholder}
                        onChange={(val) =>
                            handleTopFieldChange(criteria.key, val)
                        }
                        allowClear
                    />
                );

            default:
                return null;
        }
    };

    return (
        <SearchCriteriaView
            showMoreFilters={showMoreFilters}
            setShowMoreFilters={setShowMoreFilters}
            title={props.title}
            subtitle={props.subtitle}
            className={props.className}
            defaultLabels={defaultLabels}
            appliedFilters={appliedFilters}
            removeFilter={removeFilter}
            clearAllFilters={clearAllFilters}
            handleSearch={handleSearch}
            showReset={props.showReset}
            isSearchDisabled={!isAnythingTyped()}
            availableCriteria={props.searchCriteria || []}
            renderInputForCriteria={renderInputForCriteria}
            renderTopContent={() =>
                props.renderTopContent?.({
                    values: topValues,
                    onChange: handleTopFieldChange
                })
            }
        />
    );
};

export default SearchCriteria;
